import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Mic, Video, MessageSquare, PhoneOff,
  MonitorUp, MoreHorizontal, Stethoscope,
  ShieldCheck, Clock3, Users,
} from "lucide-react";
import videoConsult from "../assets/video-consultation.jpg";

function useDarkMode() {
  const [dark, setDark] = useState(
    () => document.documentElement.getAttribute("data-theme") === "dark"
  );
  useEffect(() => {
    const observer = new MutationObserver(() =>
      setDark(document.documentElement.getAttribute("data-theme") === "dark")
    );
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);
  return dark;
}

function ControlButton({ icon: Icon, active = false, dark = false }) {
  return (
    <button
      aria-label="Control"
      className="ctrl-btn"
      style={{
        width: "44px", height: "44px",
        borderRadius: "14px",
        border: "none",
        display: "grid", placeItems: "center",
        cursor: "pointer",
        transition: "transform 0.2s, background 0.2s",
        background: active
          ? dark
            ? "linear-gradient(135deg,#c8961e,#7a5c10)"
            : "linear-gradient(135deg,#12c4b4,#0ba88a)"
          : "rgba(255,255,255,0.14)",
        color: "#ffffff",
        boxShadow: active
          ? dark
            ? "0 4px 18px rgba(200,150,30,0.45)"
            : "0 4px 18px rgba(18,196,180,0.45)"
          : "none",
        flexShrink: 0,
      }}
      onMouseOver={e => { e.currentTarget.style.transform = "scale(1.07)"; }}
      onMouseOut={e  => { e.currentTarget.style.transform = "scale(1)"; }}
    >
      <Icon size={17} color="#fff" />
    </button>
  );
}

const STATS = [
  { key: "< 60s",   label: "Connection time",    Icon: Clock3     },
  { key: "4.9 / 5", label: "Patient Satisfaction", Icon: ShieldCheck },
  { key: "12,400+", label: "Experts Online",      Icon: Users      },
];

export function AIConsultation() {
  const dark = useDarkMode();

  const t = dark
    ? {
        sectionBg:    "linear-gradient(180deg,#0a0800 0%,#0f0c02 60%,#0a0800 100%)",
        gridColor:    "rgba(200,150,30,0.06)",
        glowBg:       "radial-gradient(ellipse,rgba(200,150,30,0.12),transparent 70%)",
        headingColor: "#f5e6b0",
        accentColor:  "#f0c040",
        badgeBorder:  "rgba(200,150,30,0.35)",
        badgeBg:      "rgba(200,150,30,0.10)",
        badgeText:    "#f0c040",
        subText:      "#b89a4a",
        statBg:       "#110d02",
        statBorder:   "rgba(200,150,30,0.22)",
        statKey:      "#f0c040",
        statLabel:    "#b89a4a",
        statShadow:   "0 2px 16px rgba(200,150,30,0.10)",
        verifiedBg:   "rgba(200,150,30,0.15)",
        verifiedText: "#f0c040",
        verifiedDot:  "#f0c040",
      }
    : {
        sectionBg:    "linear-gradient(180deg,#f0fafa 0%,#ffffff 60%,#f8fffe 100%)",
        gridColor:    "rgba(0,150,140,0.05)",
        glowBg:       "radial-gradient(ellipse,rgba(18,196,180,0.14),transparent 70%)",
        headingColor: "#0a1628",
        accentColor:  "#12c4b4",
        badgeBorder:  "rgba(18,196,180,0.35)",
        badgeBg:      "rgba(18,196,180,0.07)",
        badgeText:    "#12c4b4",
        subText:      "#64748b",
        statBg:       "#ffffff",
        statBorder:   "#e2e8f0",
        statKey:      "#0a1628",
        statLabel:    "#64748b",
        statShadow:   "0 2px 16px rgba(0,0,0,0.06)",
        verifiedBg:   "rgba(18,196,180,0.12)",
        verifiedText: "#0ba88a",
        verifiedDot:  "#0ba88a",
      };

  return (
    <section
      id="video-consult"
      style={{
        position: "relative", overflow: "hidden",
        padding: "6rem 0",
        background: t.sectionBg,
        transition: "background 0.4s",
      }}
    >
      {/* Grid overlay */}
      <div aria-hidden style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage:
          `linear-gradient(${t.gridColor} 1px,transparent 1px),` +
          `linear-gradient(90deg,${t.gridColor} 1px,transparent 1px)`,
        backgroundSize: "32px 32px", opacity: 0.6,
      }} />

      {/* Top glow */}
      <div aria-hidden style={{
        position: "absolute", top: 0, left: "50%",
        transform: "translateX(-50%)",
        width: "700px", height: "400px",
        borderRadius: "50%",
        background: t.glowBg,
        filter: "blur(40px)", pointerEvents: "none",
      }} />

      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 1.5rem" }}>

        {/* ── Header ── */}
        <div style={{ textAlign: "center", marginBottom: "3.5rem" }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            borderRadius: "9999px",
            border: `1px solid ${t.badgeBorder}`,
            background: t.badgeBg,
            padding: "5px 16px",
            fontSize: "10px", fontWeight: 700,
            letterSpacing: "0.18em", textTransform: "uppercase",
            color: t.badgeText,
            fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif",
            marginBottom: "20px",
          }}>
            <Video size={13} color={t.accentColor} /> Virtual Consultation
          </span>

          <h2 style={{
            fontFamily: "'Sora',system-ui,sans-serif",
            fontSize: "clamp(1.75rem,3.5vw,3rem)",
            fontWeight: 500, letterSpacing: "-1.5px",
            lineHeight: 1.1, color: t.headingColor,
            transition: "color 0.4s",
          }}>
            Consult with a doctor,
            <br />
            <span style={{ color: t.accentColor }}> anytime - anywhere.</span>
          </h2>

          <p style={{
            marginTop: "16px", fontSize: "14.5px",
            color: t.subText,
            lineHeight: 1.7, maxWidth: "560px", margin: "16px auto 0",
            fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif",
            transition: "color 0.4s",
          }}>
            Secure virtual consultation with trusted doctors, fast, convenient and seamlessly connected to your digital health records.
          </p>
        </div>

        {/* ── Video UI mockup ── */}
        <div style={{ position: "relative", maxWidth: "860px", margin: "0 auto" }}>

          {/* Glow behind card */}
          <div style={{
            position: "absolute", inset: "-40px", zIndex: 0,
            borderRadius: "3rem",
            background: dark ? "rgba(200,150,30,0.07)" : "rgba(18,196,180,0.08)",
            filter: "blur(30px)",
            transition: "background 0.4s",
          }} />

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            style={{
              position: "relative", zIndex: 1,
              overflow: "hidden", borderRadius: "28px",
              border: dark
                ? "1px solid rgba(200,150,30,0.20)"
                : "1px solid rgba(255,255,255,0.12)",
              background: dark ? "#0f0b02" : "#0a1f35",
              boxShadow: dark
                ? "0 32px 80px rgba(0,0,0,0.70), 0 0 40px rgba(200,150,30,0.08)"
                : "0 32px 80px rgba(0,0,0,0.55)",
              transition: "background 0.4s, border 0.4s, box-shadow 0.4s",
            }}
          >
            {/* ── Title bar ── */}
            <div className="vc-titlebar" style={{
              display: "flex", alignItems: "center",
              justifyContent: "space-between",
              borderBottom: dark
                ? "1px solid rgba(200,150,30,0.12)"
                : "1px solid rgba(255,255,255,0.08)",
              background: "rgba(0,0,0,0.40)",
              padding: "10px 16px",
              fontSize: "11px", color: "rgba(255,255,255,0.65)",
              gap: "8px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
                <span style={{ width:"10px", height:"10px", borderRadius:"50%", background:"#ef4444", display:"inline-block", flexShrink:0 }} />
                <span style={{ width:"10px", height:"10px", borderRadius:"50%", background:"#f5a623", display:"inline-block", flexShrink:0 }} />
                <span style={{ width:"10px", height:"10px", borderRadius:"50%", background:t.accentColor, display:"inline-block", flexShrink:0 }} />
                <span className="vc-titlebar-text" style={{ marginLeft:"8px", fontWeight:600, letterSpacing:"0.04em", color:"rgba(255,255,255,0.8)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                  Doxzon Video Call
                </span>
              </div>
              <div className="vc-titlebar-enc" style={{ display:"flex", alignItems:"center", gap:"5px", flexShrink:0, whiteSpace:"nowrap" }}>
                <span style={{ fontSize:"10px" }}>End-to-end encrypted</span>
                <ShieldCheck size={13} color={t.accentColor} />
              </div>
            </div>

            {/* ── Video area ── */}
            <div style={{ position: "relative" }}>
              <img
                src={videoConsult}
                alt="Doctor on a Doxzon HD video consultation"
                style={{
                  width: "100%", aspectRatio: "16/9", objectFit: "cover", display: "block",
                  filter: dark ? "brightness(0.82) sepia(0.15)" : "none",
                  transition: "filter 0.4s",
                }}
                loading="lazy"
              />

              {/* LIVE badge */}
              <div style={{
                position: "absolute", top: "12px", left: "12px",
                display: "flex", alignItems: "center", gap: "5px",
                background: "rgba(220,38,38,0.92)",
                borderRadius: "9999px", padding: "4px 10px",
                fontSize: "10px", fontWeight: 700, color: "#fff",
              }}>
                <span style={{ width:"6px", height:"6px", borderRadius:"50%", background:"#fff", display:"inline-block", animation:"ping 1.2s ease-in-out infinite" }} />
                LIVE
              </div>

              {/* Timer */}
              <div style={{
                position: "absolute", top: "12px", right: "12px",
                display: "flex", alignItems: "center", gap: "5px",
                background: "rgba(0,0,0,0.52)",
                borderRadius: "9999px", padding: "4px 10px",
                fontSize: "11px", fontWeight: 600, color: "#fff",
                backdropFilter: "blur(6px)",
              }}>
                <Clock3 size={12} color="#fff" /> 04:21
              </div>

              {/* Doctor card */}
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="vc-doctor-card"
                style={{
                  position: "absolute",
                  bottom: "72px", left: "12px",
                  display: "flex", alignItems: "center", gap: "8px",
                  background: dark ? "rgba(20,14,2,0.96)" : "rgba(255,255,255,0.96)",
                  borderRadius: "14px", padding: "8px 12px 8px 8px",
                  boxShadow: dark
                    ? "0 8px 24px rgba(0,0,0,0.40), 0 0 12px rgba(200,150,30,0.15)"
                    : "0 8px 24px rgba(0,0,0,0.18)",
                  border: dark ? "1px solid rgba(200,150,30,0.20)" : "none",
                  transition: "background 0.4s",
                  maxWidth: "calc(100% - 130px)",
                }}
              >
                <div style={{
                  width: "32px", height: "32px", borderRadius: "9px",
                  background: dark
                    ? "linear-gradient(135deg,#c8961e,#7a5c10)"
                    : "linear-gradient(135deg,#12c4b4,#0ba88a)",
                  display: "grid", placeItems: "center", flexShrink: 0,
                  transition: "background 0.4s",
                }}>
                  <Stethoscope size={14} color="#fff" />
                </div>
                <div style={{ fontSize: "11px", color: dark ? "#f5e6b0" : "#0a1628", minWidth: 0 }}>
                  <p style={{ fontWeight: 700, margin: 0, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>Dr. Eleanor Hayes, MD</p>
                  <p style={{ color: dark ? "#b89a4a" : "#64748b", margin: 0 }}>General Practitioner</p>
                </div>
                <span style={{
                  marginLeft: "4px", flexShrink: 0,
                  display: "inline-flex", alignItems: "center", gap: "3px",
                  background: t.verifiedBg,
                  borderRadius: "9999px", padding: "2px 7px",
                  fontSize: "9px", fontWeight: 700, color: t.verifiedText,
                }}>
                  <span style={{ width:"5px", height:"5px", borderRadius:"50%", background:t.verifiedDot, display:"inline-block", animation:"pulse 2s infinite" }} />
                  Verified
                </span>
              </motion.div>

              {/* PiP "You" */}
              <div className="vc-pip" style={{
                position: "absolute",
                bottom: "72px", right: "12px",
                width: "110px", height: "74px",
                borderRadius: "12px",
                border: "2px solid rgba(255,255,255,0.35)",
                background: dark
                  ? "linear-gradient(135deg,#110d02,rgba(200,150,30,0.55))"
                  : "linear-gradient(135deg,#0a2035,rgba(18,196,180,0.67))",
                display: "grid", placeItems: "center",
                fontSize: "10px", fontWeight: 700,
                letterSpacing: "0.12em", color: "rgba(255,255,255,0.8)",
                overflow: "hidden",
                transition: "background 0.4s",
              }}>
                You
              </div>

              {/* Controls bar */}
              <div className="vc-controls" style={{
                position: "absolute", bottom: 0, left: 0, right: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: "8px", padding: "12px 16px",
                background: "linear-gradient(to top,rgba(0,0,0,0.85),rgba(0,0,0,0.40),transparent)",
                flexWrap: "nowrap",
                overflowX: "auto",
              }}>
                <ControlButton icon={Mic}            dark={dark} />
                <ControlButton icon={Video}          dark={dark} active />
                <ControlButton icon={MonitorUp}      dark={dark} />
                <ControlButton icon={MessageSquare}  dark={dark} />
                <ControlButton icon={MoreHorizontal} dark={dark} />
                <button
                  className="vc-end-btn"
                  style={{
                    marginLeft: "4px",
                    display: "inline-flex", alignItems: "center", gap: "5px",
                    height: "44px", borderRadius: "14px",
                    border: "none", background: "#ef4444",
                    padding: "0 16px", fontSize: "13px",
                    fontWeight: 700, color: "#fff", cursor: "pointer",
                    boxShadow: "0 4px 18px rgba(239,68,68,0.45)",
                    transition: "transform 0.2s",
                    fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif",
                    flexShrink: 0,
                  }}
                  onMouseOver={e => e.currentTarget.style.transform = "scale(1.05)"}
                  onMouseOut={e  => e.currentTarget.style.transform = "scale(1)"}
                >
                  <PhoneOff size={15} color="#fff" /> End
                </button>
              </div>
            </div>
          </motion.div>

          {/* ── Stats row ── */}
          <div className="vc-stats" style={{
            marginTop: "28px",
            display: "grid",
            gridTemplateColumns: "repeat(3,1fr)",
            gap: "14px",
          }}>
            {STATS.map(({ key, label, Icon }) => (
              <div key={label} style={{
                borderRadius: "18px",
                border: `1px solid ${t.statBorder}`,
                background: t.statBg,
                padding: "20px 22px",
                boxShadow: t.statShadow,
                transition: "background 0.4s, border 0.4s",
              }}>
                <Icon size={18} color={t.accentColor} />
                <p style={{
                  marginTop: "12px", marginBottom: "4px",
                  fontFamily: "'Sora',system-ui,sans-serif",
                  fontSize: "1.7rem", fontWeight: 700,
                  color: t.statKey, transition: "color 0.4s",
                }}>
                  {key}
                </p>
                <p style={{
                  fontSize: "12px", color: t.statLabel,
                  fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif",
                  transition: "color 0.4s", margin: 0,
                }}>
                  {label}
                </p>
              </div>
            ))}
          </div>

        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes ping  { 0%{opacity:1} 70%,100%{opacity:0;transform:scale(1.6)} }

        /* ── Mobile (≤ 480px) ── */
        @media (max-width: 480px) {

          #video-consult { padding: 3rem 0 !important; }
          #video-consult > div > div:first-child { margin-bottom: 1.75rem !important; }

          /* Title bar */
          .vc-titlebar { padding: 8px 10px !important; }
          .vc-titlebar-enc { display: none !important; }
          .vc-titlebar-text { font-size: 10px !important; }

          /* Controls bar — compact, sits at bottom */
          .vc-controls {
            gap: 5px !important;
            padding: 6px 6px !important;
          }
          .ctrl-btn {
            width: 30px !important;
            height: 30px !important;
            border-radius: 8px !important;
          }
          .ctrl-btn svg { width: 12px !important; height: 12px !important; }
          .vc-end-btn {
            height: 30px !important;
            padding: 0 9px !important;
            font-size: 11px !important;
            border-radius: 8px !important;
            gap: 3px !important;
            margin-left: 2px !important;
          }
          .vc-end-btn svg { width: 11px !important; height: 11px !important; }

          /* Doctor card — tiny, bottom-left corner inside video */
          .vc-doctor-card {
            bottom: 8px !important;
            left: 8px !important;
            right: auto !important;
            max-width: 42% !important;
            padding: 3px 6px 3px 4px !important;
            border-radius: 7px !important;
            gap: 4px !important;
          }
          .vc-doctor-card > div:first-child {
            width: 20px !important;
            height: 20px !important;
            border-radius: 5px !important;
          }
          .vc-doctor-card > div:first-child svg { width: 10px !important; height: 10px !important; }
          .vc-doctor-card > div:nth-child(2) p { font-size: 8px !important; line-height: 1.2 !important; margin: 0 !important; }
          .vc-doctor-card > span { display: none !important; }

          /* PiP — tiny corner */
          .vc-pip {
            width: 48px !important;
            height: 34px !important;
            bottom: 8px !important;
            right: 8px !important;
            border-radius: 5px !important;
            font-size: 8px !important;
            letter-spacing: 0.05em !important;
          }

          /* Stats — 1 col */
          .vc-stats {
            grid-template-columns: 1fr !important;
            gap: 10px !important;
            margin-top: 18px !important;
          }
        }

        /* ── Small mobile (481–640px) ── */
        @media (min-width: 481px) and (max-width: 640px) {

          #video-consult { padding: 3.5rem 0 !important; }
          .vc-titlebar-enc { display: none !important; }

          .vc-controls { gap: 6px !important; padding: 8px 10px !important; }
          .ctrl-btn    { width: 34px !important; height: 34px !important; border-radius: 10px !important; }
          .vc-end-btn  { height: 34px !important; padding: 0 11px !important; font-size: 12px !important; }

          /* Doctor card — compact, bottom-left inside video */
          .vc-doctor-card {
            bottom: 10px !important;
            left: 10px !important;
            max-width: 46% !important;
            padding: 4px 8px 4px 5px !important;
            border-radius: 8px !important;
            gap: 5px !important;
          }
          .vc-doctor-card > div:first-child {
            width: 22px !important;
            height: 22px !important;
            border-radius: 6px !important;
          }
          .vc-doctor-card > div:first-child svg { width: 11px !important; height: 11px !important; }
          .vc-doctor-card > div:nth-child(2) p { font-size: 9px !important; line-height: 1.2 !important; margin: 0 !important; }
          .vc-doctor-card > span { display: none !important; }

          .vc-pip {
            width: 58px !important;
            height: 40px !important;
            bottom: 10px !important;
            right: 10px !important;
            border-radius: 7px !important;
            font-size: 9px !important;
          }

          /* Stats — 1 col */
          .vc-stats {
            grid-template-columns: 1fr !important;
            gap: 10px !important;
          }
        }

        /* ── Medium mobile / large phone (641–767px) ── */
        @media (min-width: 641px) and (max-width: 767px) {

          .vc-titlebar-enc { display: none !important; }

          .vc-controls { gap: 6px !important; padding: 10px 12px !important; }
          .ctrl-btn    { width: 38px !important; height: 38px !important; }
          .vc-end-btn  { height: 38px !important; padding: 0 13px !important; font-size: 12px !important; }

          /* Doctor card — bottom-left inside video */
          .vc-doctor-card {
            bottom: 12px !important;
            left: 10px !important;
            max-width: 48% !important;
            padding: 5px 10px 5px 6px !important;
          }
          .vc-doctor-card > span { display: none !important; }

          .vc-pip {
            width: 70px !important;
            height: 48px !important;
            bottom: 12px !important;
            right: 10px !important;
          }

          /* Stats — 3 col */
          .vc-stats { gap: 10px !important; }
        }

        /* ── Tablet (768–1023px) ── */
        @media (min-width: 768px) and (max-width: 1023px) {
          .vc-stats { gap: 10px !important; }
          .ctrl-btn { width: 40px !important; height: 40px !important; }
        }
      `}</style>
    </section>
  );
}